// The playback hand-off as a pure state machine (spec-v2.1.md, "The Director shows, the app plays"). No DOM, no
// store: src/player.ts feeds it events (the `play` directive, the Director starting and stopping a line, the video
// element's `playing` / `ended`, the user's taps, a clock) and runs the effects it returns. Unit-tested in
// playback.test.ts.
//
//   Director's `play`  -> cued     wait for its one short line to finish (or for it to never start)
//                      -> starting play() with sound; the Director goes quiet (mic paused, volume 0)
//                      -> playing  confirmed: the element fired `playing`
//                      -> ended    the Director may speak again, and asks "what do you think?"
//   no `playing` in time, or play() rejected -> blocked: a big tap-to-play, and the Director says "tap to watch"
//   the user's own tap  -> starting straight away, interrupting the Director mid-sentence
//
// v4 (spec-v4 section 8.2, V4-K1): a `wait` request is play_and_wait's. The agent's `post_tool_speech` already waited
// for the Director's line, so it skips `cued` and goes straight to `starting`; its end, stop, block or replacement
// comes out as a `{wait, token}` effect that settles the tool call (src/playwait.ts). Requests can carry a range
// (`start` / `end`, seconds): the controller seeks to `start` (again and again until it sticks: iOS Safari ignores
// early seeks) and stops at `end`, which counts as ended. When media isn't unlocked yet, a Director request goes
// straight to the tap-to-play gate instead of trying (and stalling) first.
import type { PlayDirective } from "./types";
import type { WaitOutcome } from "./playwait";

export type PlayReq = {
  url: string;
  title?: string;
  what: PlayDirective["what"];
  projectId?: string;
  sceneId?: string;
  assetId?: string;
  version?: number;
  /** "ask": when it ends, the Director asks what they think */
  then: "ask" | "none";
  /** the user tapped play (a gesture: no waiting, and the Director is interrupted) */
  user: boolean;
  /** where it plays: the editing suite's preview, the library's full-screen player, or the overlay */
  surface?: "library";
  /** v4: play_and_wait: the tool call `token` resolves when this settles; no cue wait */
  wait?: boolean;
  token?: string;
  /** v4: the item's length (the wait's guard timer) */
  durationS?: number;
  /** v4: play a range (seconds into the media): seek to `start`, stop at `end` and report it as ended */
  start?: number;
  end?: number;
};

export type PlayPhase = "idle" | "cued" | "starting" | "playing" | "blocked" | "ended";
export type PlayState = {
  phase: PlayPhase;
  req: PlayReq | null;
  /** when this phase began */
  at: number;
  /** cued: the Director's line has started */
  heard: boolean;
  /** cued: the Director is speaking right now */
  speaking: boolean;
  /** cued: when the Director's line ended */
  lineEnd: number | null;
  /** the Director is held quiet (mic paused, volume 0) */
  quiet: boolean;
};

export type PlayEvent =
  /** unlocked: false when the page has had no tap that unlocks media (a Director request then shows tap-to-play at once) */
  | { type: "request"; req: PlayReq; now: number; directorSpeaking: boolean; unlocked?: boolean }
  | { type: "director"; speaking: boolean; now: number }
  | { type: "tick"; now: number }
  | { type: "playing"; now: number }
  | { type: "rejected"; now: number }
  | { type: "tap"; now: number }
  | { type: "ended"; now: number }
  /** v4: the element failed mid-play (a network or decode error) */
  | { type: "failed"; now: number }
  | { type: "close"; now: number };

/**
 * What the controller does:
 *   load      set the element's source ahead of time
 *   play      element.play() with sound
 *   quiet     pause the Director's mic and turn its volume to 0
 *   release   undo quiet
 *   interrupt stop the Director mid-sentence ("[app] the user tapped play")
 *   say_tap   ask the Director to say "tap to watch"
 *   ask       the clip ended: ask the Director to ask what they think ("[app] finished …" user message, F3)
 *   {wait}    v4: settle the play_and_wait call `token` with this outcome
 */
export type WaitEffect = { wait: WaitOutcome; token: string };
export type PlayEffect = "load" | "play" | "quiet" | "release" | "interrupt" | "say_tap" | "ask" | WaitEffect;

/** If the Director hasn't started its line this long after the directive, play anyway. */
export const CUE_WAIT_MS = 2500;
/** Never wait longer than this for the Director's line. */
export const CUE_MAX_MS = 9000;
/** A beat after the Director's line ends before the picture starts. */
export const AFTER_LINE_MS = 350;
/** `playing` must fire this soon after play(), or autoplay was blocked. */
export const CONFIRM_MS = 2000;

export const idle = (now = 0): PlayState => ({ phase: "idle", req: null, at: now, heard: false, speaking: false, lineEnd: null, quiet: false });

type Out = { state: PlayState; effects: PlayEffect[] };
const go = (s: PlayState, patch: Partial<PlayState>, effects: PlayEffect[] = []): Out => ({ state: { ...s, ...patch }, effects });

function start(s: PlayState, now: number, interrupt: boolean): Out {
  const effects: PlayEffect[] = [];
  if (interrupt) effects.push("interrupt");
  if (!s.quiet) effects.push("quiet");
  effects.push("play");
  return go(s, { phase: "starting", at: now, quiet: true }, effects);
}

export function reducePlay(s: PlayState, e: PlayEvent): Out {
  switch (e.type) {
    case "request": {
      // a new request replaces whatever was going on (and settles a play_and_wait that was still playing)
      const pre: PlayEffect[] = waiting(s) ? [{ wait: "replaced", token: s.req!.token! }] : [];
      const base: PlayState = { ...idle(e.now), quiet: s.quiet, req: e.req };
      const out = ((): Out => {
        if (e.req.user) return start(base, e.now, e.directorSpeaking);
        // no tap has unlocked media: don't try (iOS stalls), show the tap-to-play gate now
        if (e.unlocked === false) return blocked({ ...base, phase: "starting" }, e.now, ["load"]);
        // play_and_wait: the agent already waited for the line (post_tool_speech)
        if (e.req.wait) return start(base, e.now, false);
        return go(base, { phase: "cued", at: e.now, heard: e.directorSpeaking, speaking: e.directorSpeaking, lineEnd: null }, ["load"]);
      })();
      return { state: out.state, effects: [...pre, ...out.effects] };
    }
    case "director": {
      if (s.phase !== "cued") return go(s, {});
      if (e.speaking) return go(s, { heard: true, speaking: true, lineEnd: null });
      return go(s, { speaking: false, lineEnd: s.heard ? e.now : null });
    }
    case "tick": {
      if (s.phase === "cued") {
        const lineDone = s.heard && !s.speaking && s.lineEnd !== null && e.now - s.lineEnd >= AFTER_LINE_MS;
        const neverSpoke = !s.heard && e.now - s.at >= CUE_WAIT_MS;
        const tooLong = e.now - s.at >= CUE_MAX_MS;
        if (lineDone || neverSpoke || tooLong) return start(s, e.now, tooLong && s.speaking);
        return go(s, {});
      }
      if (s.phase === "starting" && e.now - s.at >= CONFIRM_MS) return blocked(s, e.now);
      return go(s, {});
    }
    case "rejected":
      return s.phase === "starting" ? blocked(s, e.now) : go(s, {});
    case "playing":
      if (s.phase === "starting") return go(s, { phase: "playing", at: e.now });
      // a tap on the fallback, or the element got going late: hold the Director quiet again
      if (s.phase === "blocked") return go(s, { phase: "playing", at: e.now, quiet: true }, s.quiet ? [] : ["quiet"]);
      return go(s, {});
    case "tap":
      // the user wants it now: from the fallback, or without waiting for the Director's line
      // (a replay after a play_and_wait ended is the user's own: it doesn't ask again)
      if (s.phase === "ended" && s.req?.wait) return start({ ...s, req: { ...s.req, wait: false, then: "none" } }, e.now, true);
      if (s.phase === "blocked" || s.phase === "cued" || s.phase === "ended") return start(s, e.now, true);
      return go(s, {});
    case "ended": {
      if (s.phase !== "playing") return go(s, {});
      const effects: PlayEffect[] = s.quiet ? ["release"] : [];
      if (s.req?.wait && s.req.token) effects.push({ wait: "ended", token: s.req.token });
      else if (s.req?.then === "ask") effects.push("ask");
      return go(s, { phase: "ended", at: e.now, quiet: false }, effects);
    }
    case "failed": {
      if (s.phase !== "playing" && s.phase !== "starting") return go(s, {});
      const effects: PlayEffect[] = s.quiet ? ["release"] : [];
      if (waiting(s)) effects.push({ wait: "timeout", token: s.req!.token! });
      return { state: idle(e.now), effects };
    }
    case "close": {
      if (s.phase === "idle") return go(s, {});
      const effects: PlayEffect[] = s.quiet ? ["release"] : [];
      if (waiting(s)) effects.push({ wait: "stopped", token: s.req!.token! });
      return { state: idle(e.now), effects };
    }
  }
}

/** A play_and_wait request still waiting for its outcome (starting or playing). */
const waiting = (s: PlayState) => !!s.req?.wait && !!s.req.token && (s.phase === "starting" || s.phase === "playing");

function blocked(s: PlayState, now: number, pre: PlayEffect[] = []): Out {
  const effects: PlayEffect[] = [...pre];
  if (s.quiet) effects.push("release");
  // play_and_wait: the tool result tells the Director to say "tap to play"; after the tap and the end, the client
  // asks with an "[app] finished …" user message, because this tool call is already answered
  if (waiting(s)) {
    effects.push({ wait: "blocked", token: s.req!.token! });
    return go(s, { phase: "blocked", at: now, quiet: false, req: { ...s.req!, wait: false, then: "ask" } }, effects);
  }
  // the Director needs its voice back to say "tap to watch"
  effects.push("say_tap");
  return go(s, { phase: "blocked", at: now, quiet: false }, effects);
}

/** The `play` directive as a request (the Director showed it: wait for its line, then ask). */
export function fromDirective(d: PlayDirective): PlayReq {
  const req: PlayReq = { url: d.url, what: d.what, projectId: d.projectId, sceneId: d.sceneId, assetId: d.assetId, version: d.version, title: d.title, then: d.then ?? "ask", user: false };
  if (d.wait && d.token) Object.assign(req, { wait: true, token: d.token });
  if (typeof d.durationS === "number" && d.durationS > 0) req.durationS = d.durationS;
  if (typeof d.start === "number" && d.start > 0) req.start = d.start;
  if (typeof d.end === "number" && d.end > (req.start ?? 0)) req.end = d.end;
  return req;
}

/** Whether the player shows something (any phase but idle). */
export const isActive = (s: PlayState) => s.phase !== "idle";

// ------------------------------------------------------------------ v4: seeks and ranges (spec-v4-extras A11, review T4)

/** A seek within this many seconds of its target has taken. */
export const SEEK_TOLERANCE_S = 0.35;
/** Re-apply a seek at most this many times (each loadedmetadata / canplay / timeupdate is one try). */
export const MAX_SEEK_TRIES = 12;
/** A range ends this close to its `end` (timeupdate runs a few times a second; the controller also polls). */
export const RANGE_EPS_S = 0.05;

/**
 * One step of holding a seek: iOS Safari ignores `currentTime` set before playback really starts, so the controller
 * re-applies it on every media event until the element is playing at the target.
 *   seek  set currentTime = target again
 *   hold  keep watching (not playing yet, but already at the target)
 *   done  playing at (or past) the target, or out of tries: stop holding
 */
export function seekStep(target: number, current: number, started: boolean, tries: number): "seek" | "hold" | "done" {
  if (tries >= MAX_SEEK_TRIES) return "done";
  const near = Math.abs(current - target) <= SEEK_TOLERANCE_S;
  if (!started) return near ? "hold" : "seek";
  if (near || current > target + SEEK_TOLERANCE_S) return "done";
  return "seek";
}

/** Whether a range playing at `current` has reached its end. */
export const rangeDone = (current: number, end: number | undefined) => typeof end === "number" && current >= end - RANGE_EPS_S;

/** Seconds played of a request at element position `current` (a range counts from its start). */
export const playedSeconds = (req: Pick<PlayReq, "start" | "end"> | null | undefined, current: number) => {
  const from = req?.start ?? 0;
  const to = typeof req?.end === "number" ? Math.min(current, req.end) : current;
  return Math.max(0, to - from);
};
